import React, {useState, useEffect, useMemo, useCallback, useRef} from "react";
// import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
// import { customerSlice } from "./../../store";
import { AuthService, EventsService, ResourceService } from "../../services";
import Select from 'react-select';
import DatePicker from "react-datepicker";
import { Button, Modal, Spinner, Breadcrumb, BreadcrumbItem, Tabs, Tab } from "react-bootstrap";
import { pickRawSeatStorageForChart, useUnsavedChangesGuard, useFormDirtyTracker } from "../../shared";
import { navigateToMedicalCreateFromRequest } from "../../shared/utils/medicalCreateEventPayload";
import { MedicalCustomerAsyncSelect, loadMedicalAppointmentCustomerById } from "../../shared/components";

const SAME_DAY_APPOINTMENT_MESSAGE =
  "A medical appointment already exists for this customer on the same day. Do you wish to continue?";

const CreateEvent = () => {
  const navigate = useNavigate();
	// const [medicalResource, setMedicalResource] = useState(undefined);
  // const [currentResource, setCurrentResource] = useState(undefined);
  const [resources, setResources] = useState([]);
  const [customerName, setCustomerName] = useState(undefined);
  const [currentCustomer, setCurrentCustomer] = useState(undefined);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [sameDayDuplicateModalOpen, setSameDayDuplicateModalOpen] = useState(false);
  const [isCheckingSameDay, setIsCheckingSameDay] = useState(false);
  const pendingCreateEventRef = useRef(null);
  const customerFetchRef = useRef(0);
  const [providerSelect, setProviderSelect] = useState(undefined);
  const [currentResource, setCurrentResource] = useState(undefined);
  const [startTime, setStartTime] = useState('');
  const [newPatient, setNewPatient] = useState('');
  const [fasting, setFasting] = useState('');
  const [interpreter, setInterpreter] = useState('');
  const [docOrder, setDocOrder] = useState('');
  const [needId, setNeedId] = useState('');
  const [needMedicationList, setNeedMedicationList] = useState('');
  const [transMethod, setTransMethod] = useState('');
  const [reason, setReason] = useState('');
  const [color, setColor] = useState('');

  const { isDirty, resetBaseline } = useFormDirtyTracker({
    customerId: customerName?.value ?? '',
    resourceId: currentResource?.id ?? providerSelect?.value ?? '',
    startTime,
    newPatient,
    fasting,
    interpreter,
    docOrder,
    needId,
    needMedicationList,
    transMethod,
    reason,
    color,
  });
  const { tryNavigate, tryGoBack } = useUnsavedChangesGuard(isDirty);

  const providerSelectOptions = useMemo(
    () => [
      { value: "", label: "" },
      ...resources
        .filter((r) => r?.status === "active")
        .map((r) => ({
          value: r?.id || "",
          label: r?.name || "",
        })),
    ],
    [resources]
  );

 
  const params = new URLSearchParams(window.location.search);
  const redirectToCalendar = () => {
		tryNavigate(`/medical/events/calendar`);
  }

  const redirectToView = (id) => {
		resetBaseline();
		tryNavigate(`/medical/events/${id}`);
  }

  const goBack = () => {
    tryGoBack(AuthService.getMedicalOneDayListPathOrFallback());
  }

  const goToNext = (id) => {
    resetBaseline();
    tryNavigate(`/medical/events/edit/${id}?from=create`)
  }

  const validateEvent = () => {
    const errors = [];
    
    // Required fields validation
    if (customerLoading) {
      errors.push('Customer (still loading)');
    }
    if (!currentCustomer || !currentCustomer.id) {
      errors.push('Customer');
    }
    if (!startTime) {
      errors.push('Appointment Time');
    }
    if (!currentResource || !currentResource.id) {
      errors.push('Provider');
    }
    if (!newPatient) {
      errors.push('New Patient');
    }
    if (!docOrder) {
      errors.push('Order/Referral');
    }
    if (!fasting) {
      errors.push('Fasting Required');
    }
    if (!needId) {
      errors.push('ID Needed');
    }
    if (!needMedicationList) {
      errors.push('Need Medication List');
    }
    if (!interpreter) {
      errors.push('Language Support');
    }
    if (!transMethod) {
      errors.push('Transportation Support');
    }
    if (!reason || reason.trim() === '') {
      errors.push('Reason');
    }

    if (errors.length > 0) {
      window.alert(`Please fill in the following required fields:\n${errors.join('\n')}`);
      return false;
    }
    return true;
  };

  const proceedAfterCreateSave = (newEvent) => {
    resetBaseline();
    navigateToMedicalCreateFromRequest(navigate, newEvent);
  };

  const saveEvent = async () => {
    if (isCheckingSameDay) return;
    if (!validateEvent()) {
      return;
    }

    const newEventData = {
      customer: currentCustomer.id,
      resource: currentResource.id,
      client_name: currentCustomer.name,
      client_pharmacy: currentCustomer.pharmacy,
      client_pcp: currentCustomer.care_provider,
      client_birth_date: currentCustomer.birth_date,
      client_seating: pickRawSeatStorageForChart(currentCustomer) || currentCustomer.seating,
      resource_type: currentResource.type,
      resource_name: currentResource.name,
      resource_phone: currentResource.phone,
      resource_contact: currentResource.contact,
      resource_address: currentResource.address,
      resource_city: currentResource.city,
      resource_state: currentResource.state,
      new_patient: newPatient,
      fasting,
      interpreter,
      doc_order: docOrder,
      need_id: needId,
      need_med_list: needMedicationList,
      reason: reason.trim(),
      trans_method: transMethod,
      disability: currentCustomer.disability ? 'yes' : 'no',
    }

    const newEvent = {
      data: newEventData,
      color,
      start_time: new Date(startTime),
      type: 'medical',
      stop_time: new Date(startTime),
      source_type: currentResource.type,
      source_uuid: currentResource.id,
      source_name: currentResource.name,
      target_type: currentCustomer.type,
      target_uuid: currentCustomer.id,
      target_name: currentCustomer.name,
      create_by: localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name,
      create_date: new Date(),
      edit_by: localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name,
      edit_date: new Date(),
      edit_history:[{ employee: localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name, date: new Date() }]
    }

    setIsCheckingSameDay(true);
    try {
      const hasSameDayAppointment = await EventsService.customerHasMedicalAppointmentOnDate(
        currentCustomer.id,
        startTime
      );
      if (hasSameDayAppointment) {
        pendingCreateEventRef.current = newEvent;
        setSameDayDuplicateModalOpen(true);
        return;
      }
    } catch (err) {
      console.error('Failed to check same-day medical appointments', err);
    } finally {
      setIsCheckingSameDay(false);
    }

    proceedAfterCreateSave(newEvent);
  };
  

  useEffect(() => {
    if (!AuthService.canEditMedicalEvents()) {
      window.alert('You haven\'t login yet OR this user does not have access to this page. Please change an admin account to login.')
      AuthService.logout();
      navigate(`/login`);
    }
    ResourceService.getAll().then((resourcesData) => {
      setResources(resourcesData.data);
    })
  }, []);

  const onCustomerChange = useCallback((selectedCustomer) => {
    setCustomerName(selectedCustomer || undefined);

    if (!selectedCustomer?.value) {
      setCurrentCustomer(undefined);
      setCustomerLoading(false);
      return;
    }

    const fetchId = ++customerFetchRef.current;
    setCurrentCustomer(undefined);
    setCustomerLoading(true);

    loadMedicalAppointmentCustomerById(selectedCustomer.value).then((customer) => {
      if (customerFetchRef.current !== fetchId) return;
      setCustomerLoading(false);
      if (customer) {
        setCurrentCustomer(customer);
        return;
      }
      window.alert("Could not load customer details. Please try again.");
      setCustomerName(undefined);
    });
  }, []);

  const onProviderChange = (selected) => {
    setProviderSelect(selected);
    setCurrentResource(selected ? resources.find((r) => r.id === selected.value) : undefined);
  };

  return (
    <>
      <div className="list row mb-4">
        <Breadcrumb>
          <Breadcrumb.Item href="/dashboard/dashboard">Medical</Breadcrumb.Item>
          <Breadcrumb.Item href={AuthService.getMedicalOneDayListPathOrFallback()}>
            Medical Appointment Information
          </Breadcrumb.Item>
          <Breadcrumb.Item active>
            Create New Medical Appointment
          </Breadcrumb.Item>
        </Breadcrumb>
        <div className="col-md-12 text-primary">
           <h4>Create New Medical Appointment <button className="btn btn-link btn-sm" onClick={() => {goBack()}}>Back</button></h4> 
        </div>
      </div>

      <div className="app-main-content-list-container form-page">
				<div className="app-main-content-list-func-container">
					<Tabs defaultActiveKey="eventClientInfo" id="event-tab">
          <Tab eventKey="eventClientInfo" title="Appointment Customer And Time Information">
              <div className="multi-columns-container">
                <div className="column-container">
                  <div className="column-card">
                    <h6 className="text-primary">Customer And Appointment Time</h6>
                    <div className="app-main-content-fields-section">
                      <div className="me-4">
                        <div className="field-label">Customer
                          <span className="required">*</span>
                        </div>
                        <MedicalCustomerAsyncSelect
                          value={customerName || null}
                          onChange={onCustomerChange}
                        />
                      </div>
                      <div className="me-4">
                        <div className="field-label">Provider
                          <span className="required">*</span>
                        </div>
                        <Select styles={{
                          control: (baseStyles) => ({
                            ...baseStyles,
                            width: '350px',
                            borderRadius: '8px'
                          }),
                          indicatorSeparator: () => ({
                            display: 'none'
                          }),
                          dropdownIndicator: (baseStyles) => ({
                            ...baseStyles,
                            paddingRight: '8px',
                            color: '#333',
                            '&:hover': { color: '#000' }
                          }),
                          valueContainer: (baseStyles) => ({
                            ...baseStyles,
                            height: '43px',
                            padding: '0 8px',
                          }),
                          input: (baseStyles) => ({
                            ...baseStyles,
                            margin: '0px',
                            padding: '0px',
                            height: '30px',
                            width: '290px'
                          }),
                          placeholder: (baseStyles) => ({
                            ...baseStyles,
                            fontSize: '13px'
                          }),
                          singleValue: (baseStyles) => ({
                            ...baseStyles,
                            margin: '0px',
                            fontSize: '13px'
                          })
                        }} value={providerSelect || null} onChange={onProviderChange} options={providerSelectOptions}></Select>
                      </div>
                      <div className="me-4">
                        <div className="field-label">Appointment Time
                          <span className="required">*</span>
                        </div>
                        <DatePicker
                          selected={startTime}
                          onChange={setStartTime}
                          showTimeInput
                          timeInputLabel="Time:"
                          dateFormat="MM/dd/yyyy, HH:mm"
                        ></DatePicker>
                      </div>
                    </div>
                    <h6 className="text-primary">Appointment Details</h6>
                    <div className="app-main-content-fields-section">
                      <div className="me-4">
                        <div className="field-label">New Patient<span className="required">*</span></div>
                        <select value={newPatient} onChange={(e) => setNewPatient(e.target.value)}>
                          <option value="">Select...</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      </div>
                      <div className="me-4">
                        <div className="field-label">Order/Referral<span className="required">*</span></div>
                        <select value={docOrder} onChange={(e) => setDocOrder(e.target.value)}>
                          <option value="">Select...</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      </div>
                      <div className="me-4">
                        <div className="field-label">Fasting Required<span className="required">*</span></div>
                        <select value={fasting} onChange={(e) => setFasting(e.target.value)}>
                          <option value="">Select...</option>
                          <option value="empty_stomach">Empty Stomach</option>
                          <option value="clear_liquid">Clear Liquid</option>
                          <option value="no">No</option>
                        </select>
                      </div>
                    </div>
                    <div className="app-main-content-fields-section">
                      <div className="me-4">
                        <div className="field-label">ID Needed<span className="required">*</span></div>
                        <select value={needId} onChange={(e) => setNeedId(e.target.value)}>
                          <option value="">Select...</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      </div>
                      <div className="me-4">
                        <div className="field-label">Need Medication List<span className="required">*</span></div>
                        <select value={needMedicationList} onChange={(e) => setNeedMedicationList(e.target.value)}>
                          <option value="">Select...</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      </div>
                    </div>
                    <h6 className="text-primary">Assistance</h6>
                    <div className="app-main-content-fields-section">
                      <div className="me-4">
                        <div className="field-label">Language Support<span className="required">*</span></div>
                        <select value={interpreter} onChange={(e) => setInterpreter(e.target.value)}>
                          <option value="">Select...</option>
                          {EventsService.interpreterLevelOptions?.map((item) => (
                            <option key={item.value} value={item.value}>{item.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="me-4">
                        <div className="field-label">Transportation Support<span className="required">*</span></div>
                        <select value={transMethod} onChange={(e) => setTransMethod(e.target.value)}>
                          <option value="">Select...</option>
                          {EventsService.transportationTypeOptions?.map((item) => (
                            <option key={item.value} value={item.value}>{item.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <h6 className="text-primary">Additional Information</h6>
                    <div className="app-main-content-fields-section">
                      <div className="me-4">
                        <div className="field-label">Reason<span className="required">*</span></div>
                        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={4} style={{ width: '400px' }} placeholder="Enter reason for visit" />
                      </div>
                    </div>
                    <div className="list row mb-5">
                      <div className="col-md-12 col-sm-12 col-xs-12">
                        <button className="btn btn-default btn-sm float-right" onClick={() => goBack()}> Cancel </button>
                        <button
                          className="btn btn-primary btn-sm float-right"
                          onClick={() => saveEvent()}
                          disabled={isCheckingSameDay}
                        >
                          {isCheckingSameDay ? "Checking…" : "Save"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="column-container">
                  <div className="column-card">
                  {
                    customerLoading && (
                      <div className="text-center py-4">
                        <Spinner animation="border" size="sm" role="status" />
                        <div className="text-muted small mt-2">Loading customer information…</div>
                      </div>
                    )
                  }
                  {
                    currentCustomer && !customerLoading && <>
                      <h6 className="text-primary">Customer Information</h6>
                        <div className="app-main-content-fields-section short">
                          <div className="field-body">
                            <div className="field-label">Customer Name</div> 
                            <div className="field-value">{currentCustomer?.name || ''}</div>
                          </div>
                          <div className="field-body">
                            <div className="field-label">Preferred Name</div> 
                            <div className="field-value">{currentCustomer?.name_cn || ''}</div>
                          </div>
                          <div className="field-body">
                            <div className="field-label">Pharmacy</div> 
                            <div className="field-value">{currentCustomer?.pharmacy || ''}</div>
                          </div>
                        </div>
                        <div className="app-main-content-fields-section short">
                          <div className="field-body">
                            <div className="field-label">Care Provider</div> 
                            <div className="field-value">{currentCustomer?.care_provider || ''}</div>
                          </div>
                          <div className="field-body">
                            <div className="field-label">Birthday</div> 
                            <div className="field-value">{currentCustomer?.birth_date || ''}</div>
                          </div>
                          <div className="field-body">
                            <div className="field-label">Note</div> 
                            <div className="field-value">{currentCustomer?.note || ''}</div>
                          </div>
                        </div>
                    </>
                  }
                  </div>
                </div>
              </div>
            </Tab>
          </Tabs>
        </div>
      </div>

      <Modal
        show={sameDayDuplicateModalOpen}
        onHide={() => {
          pendingCreateEventRef.current = null;
          setSameDayDuplicateModalOpen(false);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Existing appointment</Modal.Title>
        </Modal.Header>
        <Modal.Body>{SAME_DAY_APPOINTMENT_MESSAGE}</Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              pendingCreateEventRef.current = null;
              setSameDayDuplicateModalOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              const pendingEvent = pendingCreateEventRef.current;
              pendingCreateEventRef.current = null;
              setSameDayDuplicateModalOpen(false);
              if (pendingEvent) {
                proceedAfterCreateSave(pendingEvent);
              }
            }}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CreateEvent;